"use client";

import { useState, createContext, useContext } from "react";
import { useSequentialKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { QuickJobModal } from "./QuickJobModal";
import { QuickQuoteModal } from "./QuickQuoteModal";
import { QuickInvoiceModal } from "./QuickInvoiceModal";
import { QuickClientModal } from "./QuickClientModal";

interface QuickActionsContextValue {
  openJobModal: () => void;
  openQuoteModal: () => void;
  openInvoiceModal: () => void;
  openClientModal: () => void;
}

const QuickActionsContext = createContext<QuickActionsContextValue | null>(null);

export function useQuickActions() {
  const context = useContext(QuickActionsContext);
  if (!context) {
    throw new Error("useQuickActions must be used within QuickActionsProvider");
  }
  return context;
}

export function QuickActionsProvider({ children }: { children: React.ReactNode }) {
  const [showJobModal, setShowJobModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);

  // Register keyboard shortcuts
  useSequentialKeyboardShortcuts([
    {
      sequence: ["n", "j"],
      description: "New Job",
      callback: () => setShowJobModal(true),
    },
    {
      sequence: ["n", "q"],
      description: "New Quote",
      callback: () => setShowQuoteModal(true),
    },
    {
      sequence: ["n", "i"],
      description: "New Invoice",
      callback: () => setShowInvoiceModal(true),
    },
    {
      sequence: ["n", "c"],
      description: "New Client",
      callback: () => setShowClientModal(true),
    },
  ]);

  const contextValue: QuickActionsContextValue = {
    openJobModal: () => setShowJobModal(true),
    openQuoteModal: () => setShowQuoteModal(true),
    openInvoiceModal: () => setShowInvoiceModal(true),
    openClientModal: () => setShowClientModal(true),
  };

  return (
    <QuickActionsContext.Provider value={contextValue}>
      {children}

      {/* Quick Action Modals */}
      <QuickJobModal
        isOpen={showJobModal}
        onClose={() => setShowJobModal(false)}
      />
      <QuickQuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />
      <QuickInvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
      />
      <QuickClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
      />
    </QuickActionsContext.Provider>
  );
}
